import { Divider, Grid } from "@mui/material";
import EntaBackdrop from "common/components/EntaBackdrop";
import EntaLeavingPrompt from "common/components/EntaLeavingPrompt";
import { CONFIRMATION_MESSAGE } from "developer/formsCRUD/constants";
import { Dispatch, SetStateAction, useCallback, useTransition } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { EditorModeTab } from "../constants";
import useSaveEditorContent from "../hooks/useSaveEditorContent";
import { cancelSaveWithErrorsConfirmation } from "../store/editorSlice";
import {
  displayFullscreen,
  onToggleShowKeyboardActions,
} from "../store/toolbar";
import EditorModeRenderer from "./EditorModeRenderer";
import EditorToolbar from "./EditorToolbar/EditorToolbar";
import ConfirmationDialog from "./confirmationDialog";
import FormEditorConfig from "./formEditorConfig";

interface IEditorToolbarGridProps {
  tabSelected: EditorModeTab;
  setTabSelected: Dispatch<SetStateAction<EditorModeTab>>;
}

const EditorGrid = (props: IEditorToolbarGridProps) => {
  const dispatch = useAppDispatch();
  const { tabSelected, setTabSelected } = props;
  const [isPending, startTransition] = useTransition();

  const confirmSaveWithErrors = useAppSelector(
    (state) => state.developer.edit.editor.confirmSaveWithErrors
  );

  const isDirty = useAppSelector(
    (state) => state.developer.edit.editor.isDirty
  );

  const doSave = useSaveEditorContent();

  const handleShowPreview = useCallback(() => {
    startTransition(() => {
      setTabSelected(EditorModeTab.Preview);
    });
  }, [setTabSelected]);

  const handleShowCodeMode = useCallback(() => {
    startTransition(() => {
      setTabSelected(EditorModeTab.CodeEditor);
    });
  }, [setTabSelected]);

  const handleShowVisualMode = useCallback(() => {
    startTransition(() => {
      setTabSelected(EditorModeTab.VisualEditor);
    });
  }, [setTabSelected]);

  const handleShowFullscreen = useCallback(() => {
    dispatch(displayFullscreen());
  }, [dispatch]);

  const handleOnCloseConfirmationDialog = useCallback(
    (forceSave: boolean) => {
      if (forceSave) doSave();
      else dispatch(cancelSaveWithErrorsConfirmation());
    },
    [dispatch, doSave]
  );

  const handleShowKeyboardActions = useCallback(() => {
    dispatch(onToggleShowKeyboardActions(true));
  }, [dispatch]);

  return (
    <EntaLeavingPrompt message={CONFIRMATION_MESSAGE} isUpdatePending={isDirty}>
      <FormEditorConfig />
      <EntaBackdrop isOpen={isPending} />
      <Grid container direction="column" sx={{ height: "100%" }}>
        <Grid item>
          <EditorToolbar
            tabSelected={tabSelected}
            onVisualEditorClick={handleShowVisualMode}
            onFullscreenClick={handleShowFullscreen}
            onSaveClick={doSave}
            onCodeEditorClick={handleShowCodeMode}
            onPreviewClick={handleShowPreview}
            onShowKeyboardActions={handleShowKeyboardActions}
          />
        </Grid>
        <Divider />
        <Grid item xs sx={{ overflow: "hidden" }}>
          <EditorModeRenderer tabSelected={tabSelected} />
        </Grid>
      </Grid>
      <ConfirmationDialog
        isOpen={confirmSaveWithErrors}
        onClose={handleOnCloseConfirmationDialog}
      />
    </EntaLeavingPrompt>
  );
};

export default EditorGrid;
